import React, { useState, useEffect, useMemo } from 'react';
import { 
  ChevronLeft, ChevronRight, Navigation, Calendar,
  Loader, Clock, Phone, MapPin, CheckCircle, Sparkles, 
  TrendingUp, Route, X, ArrowRight, AlertCircle
} from 'lucide-react';
import { DndContext, DragOverlay, pointerWithin, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import JobBlock from './JobBlock';
import WeatherBadge from './WeatherBadge';
import ConflictModal from './ConflictModal';
import DayDetailView from './DayDetailView';
import { useWeatherForDates } from './useWeather';
import { formatCurrency } from '../../utils/helpers';
import routingService from '../../services/routingService';
import businessService from '../../services/businessService';

// ============================================
// HELPERS
// ============================================

// Get local date string in YYYY-MM-DD format
const getLocalDateString = (date = new Date()) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// Get start of week (Sunday)
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  return d;
};

// Get array of dates for the week
const getWeekDates = (startDate) => {
  const dates = [];
  const start = getWeekStart(startDate);
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    dates.push(getLocalDateString(d));
  }
  return dates;
};

// Get array of dates for the month (including padding days)
const getMonthDates = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  
  // First day of the month
  const firstDay = new Date(year, month, 1);
  // Last day of the month
  const lastDay = new Date(year, month + 1, 0);
  
  // Start from the Sunday of the week containing the first day
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());
  
  // End on the Saturday of the week containing the last day
  const endDate = new Date(lastDay);
  endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));
  
  const dates = [];
  const current = new Date(startDate);
  
  while (current <= endDate) {
    dates.push(getLocalDateString(current));
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
};

// Format date for display
const formatDayHeader = (dateStr, isToday) => {
  const date = new Date(dateStr + 'T12:00:00');
  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
  const dayNum = date.getDate();
  return { dayName, dayNum, isToday };
};

// Parse time from job's scheduledDate - returns HH:MM as stored in DB (LOCAL time)
const getJobTime = (job) => {
  if (!job.scheduledDate) return null;
  
  // Your system stores times as LOCAL, not UTC
  // The DB value like "08:00:00" means 8:00 AM local
  // We just need to extract the time portion directly
  const timePart = job.scheduledDate.split('T')[1];
  if (!timePart) return null;
  
  const [hours, minutes] = timePart.split(':');
  const h = parseInt(hours);
  const m = parseInt(minutes);
  
  // Check if midnight (no time set)
  if (h === 0 && m === 0) {
    return null;
  }
  
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// Format time for display - returns "8:00 AM" format
const formatTimeDisplay = (job) => {
  const time = getJobTime(job);
  if (!time) return null;
  
  const [hours, minutes] = time.split(':').map(Number);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`;
};

// Get job date (YYYY-MM-DD)
const getJobDate = (job) => {
  if (!job.scheduledDate) return null;
  return job.scheduledDate.split('T')[0];
};

// ============================================
// STATUS LEGEND COMPONENT
// ============================================

const StatusLegend = () => (
  <div className="flex items-center gap-3 text-xs text-gray-500">
    <span className="font-medium">Status:</span>
    <div className="flex items-center gap-1">
      <span className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center text-white text-[8px] font-bold">S</span>
      <span>Scheduled</span>
    </div>
    <div className="flex items-center gap-1">
      <span className="w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center text-white text-[8px] font-bold">I</span>
      <span>In Progress</span>
    </div>
    <div className="flex items-center gap-1">
      <span className="w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center text-white text-[8px] font-bold">Q</span>
      <span>Quoted</span>
    </div>
    <div className="flex items-center gap-1">
      <span className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center text-white text-[8px] font-bold">C</span>
      <span>Completed</span>
    </div>
  </div>
);

// ============================================
// OPTIMIZE PREVIEW MODAL - Shows before/after comparison
// ============================================

const OptimizePreviewModal = ({ 
  isOpen, 
  onClose, 
  currentJobs, 
  optimizedData, 
  onStartNavigation,
  isOwner,
  isOptimizing 
}) => {
  if (!isOpen) return null;

  const currentOrder = currentJobs
    .filter(j => j.status === 'Scheduled' || j.status === 'In Progress')
    .sort((a, b) => {
      const timeA = getJobTime(a) || '99:99';
      const timeB = getJobTime(b) || '99:99';
      return timeA.localeCompare(timeB);
    });

  // Calculate current route stats (estimated)
  const currentStats = useMemo(() => {
    if (currentOrder.length < 2) return { distance: 0, time: 0 };
    // We don't have exact distance without API call, show as "calculating"
    return { distance: null, time: null };
  }, [currentOrder]);

  const optimizedJobs = optimizedData?.optimizedJobs || [];
  const savings = optimizedData ? {
    distance: optimizedData.totalDistance,
    time: optimizedData.totalDurationMinutes,
    revenuePerHour: optimizedData.revenuePerHour,
    priority: optimizedData.optimizationPriority
  } : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal Content - Bottom sheet on mobile */}
      <div className="relative bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-green-600 px-4 py-3 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={20} />
            <h3 className="font-semibold">Smart Route Optimization</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isOptimizing ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader size={40} className="text-emerald-500 animate-spin mb-4" />
              <p className="text-gray-600">Calculating optimal route...</p>
              <p className="text-xs text-gray-400 mt-1">Using your GPS location</p>
            </div>
          ) : optimizedData ? (
            <>
              {/* Savings Banner */}
              <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-emerald-800 font-semibold flex items-center gap-2">
                    <TrendingUp size={18} />
                    Optimized Route
                  </span>
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                    {savings.priority || 'Balanced'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-2xl font-bold text-emerald-700">
                      {savings.distance?.toFixed(1)}
                    </div>
                    <div className="text-xs text-emerald-600">miles</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-700">
                      {Math.floor(savings.time / 60)}h {savings.time % 60}m
                    </div>
                    <div className="text-xs text-emerald-600">total time</div>
                  </div>
                  {isOwner && (
                    <div>
                      <div className="text-2xl font-bold text-emerald-700">
                        {formatCurrency(savings.revenuePerHour)}
                      </div>
                      <div className="text-xs text-emerald-600">per hour</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Optimized Order */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Route size={16} />
                  Optimized Order
                </h4>
                <div className="space-y-2">
                  {optimizedJobs.map((job, index) => (
                    <div 
                      key={job.jobId}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100"
                    >
                      <div className="w-7 h-7 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {job.customerName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {job.address}, {job.city}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {isOwner && (
                          <p className="font-semibold text-emerald-600">
                            {formatCurrency(job.quoteAmount)}
                          </p>
                        )}
                        {job.distanceFromPrevious > 0 && (
                          <p className="text-xs text-gray-400">
                            {job.distanceFromPrevious.toFixed(1)} mi
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Current vs Optimized comparison */}
              {currentOrder.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-500 mb-2">Your scheduled order:</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {currentOrder.map((job, index) => (
                      <React.Fragment key={job.id}>
                        <span className="text-xs bg-white px-2 py-1 rounded border border-gray-200 truncate max-w-[100px]">
                          {job.customer?.name || 'Customer'}
                        </span>
                        {index < currentOrder.length - 1 && (
                          <ArrowRight size={12} className="text-gray-300 flex-shrink-0" />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle size={40} className="text-gray-300 mb-4" />
              <p className="text-gray-600">Unable to optimize route</p>
              <p className="text-xs text-gray-400 mt-1">Please try again</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {optimizedData && !isOptimizing && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onStartNavigation}
              className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <Navigation size={18} />
              Navigate with Optimized Route
            </button>
            <button
              onClick={onClose}
              className="w-full mt-2 text-gray-600 font-medium py-2 text-sm"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// DROPPABLE DAY COLUMN (Week View)
// ============================================

const DroppableDayColumn = ({ date, children, isToday }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${date}`,
    data: { date },
  });

  return (
    <div
      ref={setNodeRef}
      className={`
        flex-1 min-w-[140px] border-r border-gray-100 last:border-r-0
        ${isOver ? 'bg-primary-50' : ''}
        ${isToday ? 'bg-blue-50/30' : ''}
        transition-colors
      `}
    >
      {children}
    </div>
  );
};

// ============================================
// DROPPABLE DAY CELL (Month View)
// ============================================

const DroppableDayCell = ({ date, children, isToday, isCurrentMonth }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${date}`,
    data: { date },
  });

  return (
    <div
      ref={setNodeRef}
      className={`
        min-h-[100px] border-b border-r border-gray-100 p-1
        ${isOver ? 'bg-primary-50' : ''}
        ${isToday ? 'bg-blue-50' : ''}
        ${!isCurrentMonth ? 'bg-gray-50/50' : ''}
        transition-colors
      `}
    >
      {children}
    </div>
  );
};

// ============================================
// DAY COLUMN CONTENT (Week View)
// ============================================

const DayColumnContent = ({
  date,
  jobs,
  weather,
  isToday,
  onJobClick,
  onJobComplete,
  onJobEdit,
  onJobNavigate,
  onJobCall,
  onDayClick,
  isOwner,
}) => {
  const { dayName, dayNum } = formatDayHeader(date, isToday);
  
  // Separate jobs with time vs unscheduled (has date but no time)
  const scheduledJobs = jobs
    .filter(j => getJobTime(j))
    .sort((a, b) => {
      const timeA = getJobTime(a) || '99:99';
      const timeB = getJobTime(b) || '99:99';
      return timeA.localeCompare(timeB);
    });
  
  const unscheduledJobs = jobs.filter(j => !getJobTime(j));
  
  // Calculate daily totals
  const dailyTotal = jobs.reduce((sum, j) => sum + (j.quoteAmount || 0), 0);
  const jobCount = jobs.length;

  return (
    <div className="h-full flex flex-col group">
      {/* Day Header - Clickable to open day detail */}
      <div 
        className={`
          sticky top-0 z-10 px-2 py-2 border-b border-gray-100 bg-white cursor-pointer
          hover:bg-gray-50 transition-colors
          ${isToday ? 'bg-blue-50 hover:bg-blue-100' : ''}
        `}
        onClick={() => onDayClick?.(date)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`
              flex flex-col items-center justify-center w-10 h-10 rounded-full
              ${isToday ? 'bg-primary-600 text-white' : 'text-gray-600'}
            `}>
              <span className="text-xs font-medium leading-none">{dayName}</span>
              <span className="text-lg font-bold leading-none">{dayNum}</span>
            </div>
          </div>
          
          {/* Weather - subtle */}
          <WeatherBadge weather={weather} compact />
        </div>
        
        {/* Daily summary */}
        <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
          <span>{jobCount} job{jobCount !== 1 ? 's' : ''}</span>
          {isOwner && dailyTotal > 0 && (
            <span className="font-medium text-gray-700">{formatCurrency(dailyTotal)}</span>
          )}
        </div>
      </div>

      {/* Jobs List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {scheduledJobs.length === 0 && unscheduledJobs.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            No jobs
          </div>
        ) : (
          <>
            {/* Scheduled jobs (with time) */}
            {scheduledJobs.map((job) => (
              <JobBlock
                key={job.id}
                job={job}
                compact
                onClick={onJobClick}
                onComplete={onJobComplete}
                onEdit={onJobEdit}
                onNavigate={onJobNavigate}
                onCall={onJobCall}
                isOwner={isOwner}
              />
            ))}
            
            {/* Unscheduled jobs (date only, no time) */}
            {unscheduledJobs.length > 0 && (
              <>
                {scheduledJobs.length > 0 && (
                  <div className="text-xs text-gray-400 text-center py-1 mt-2">
                    — No time set —
                  </div>
                )}
                {unscheduledJobs.map((job) => (
                  <JobBlock
                    key={job.id}
                    job={job}
                    compact
                    onClick={onJobClick}
                    onComplete={onJobComplete}
                    onEdit={onJobEdit}
                    onNavigate={onJobNavigate}
                    onCall={onJobCall}
                    isOwner={isOwner}
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============================================
// DAY CELL CONTENT (Month View)
// ============================================

const DayCellContent = ({
  date,
  jobs,
  weather,
  isToday,
  isCurrentMonth,
  onJobClick,
  onDayClick,
  isOwner,
}) => {
  const dayNum = new Date(date + 'T12:00:00').getDate();
  const dailyTotal = jobs.reduce((sum, j) => sum + (j.quoteAmount || 0), 0);
  
  // Sort jobs by time
  const sortedJobs = [...jobs].sort((a, b) => {
    const timeA = getJobTime(a) || '99:99';
    const timeB = getJobTime(b) || '99:99';
    return timeA.localeCompare(timeB);
  });

  return (
    <div 
      className={`h-full flex flex-col cursor-pointer ${!isCurrentMonth ? 'opacity-40' : ''}`}
      onClick={() => onDayClick?.(date)}
    >
      {/* Day number header */}
      <div className="flex items-center justify-between px-1 py-0.5">
        <span className={`
          text-sm font-medium w-6 h-6 flex items-center justify-center rounded-full
          ${isToday ? 'bg-primary-600 text-white' : 'text-gray-700'}
        `}>
          {dayNum}
        </span>
        {weather && <WeatherBadge weather={weather} compact />}
      </div>
      
      {/* Jobs (show max 3, then "+X more") */}
      <div className="flex-1 px-1 space-y-0.5 overflow-hidden">
        {sortedJobs.slice(0, 3).map((job) => (
          <div
            key={job.id}
            onClick={(e) => {
              e.stopPropagation();
              onJobClick?.(job);
            }}
            className={`
              text-xs px-1.5 py-0.5 rounded truncate cursor-pointer
              ${job.status === 'Scheduled' ? 'bg-blue-100 text-blue-800' : ''}
              ${job.status === 'In Progress' ? 'bg-purple-100 text-purple-800' : ''}
              ${job.status === 'Quoted' ? 'bg-amber-100 text-amber-800' : ''}
              ${job.status === 'Completed' ? 'bg-green-100 text-green-800' : ''}
              hover:opacity-80
            `}
          >
            {formatTimeDisplay(job) && (
              <span className="font-medium">{formatTimeDisplay(job)} </span>
            )}
            {job.customer?.name}
          </div>
        ))}
        {sortedJobs.length > 3 && (
          <div className="text-xs text-gray-500 px-1">
            +{sortedJobs.length - 3} more
          </div>
        )}
      </div>
      
      {/* Daily total */}
      {isOwner && dailyTotal > 0 && (
        <div className="text-xs font-medium text-gray-600 px-1 pb-0.5 text-right">
          {formatCurrency(dailyTotal)}
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPACT MOBILE JOB ROW (for mobile day view)
// ============================================

const MobileJobRow = ({
  job,
  onJobClick,
  onJobComplete,
  onJobNavigate,
  onJobCall,
  isOwner,
}) => {
  const time = getJobTime(job);
  const timeDisplay = time ? formatTimeDisplay(job) : null;
  const canComplete = job.status === 'Scheduled' || job.status === 'In Progress';
  
  const statusColors = {
    Scheduled: 'bg-blue-500',
    'In Progress': 'bg-purple-500',
    Quoted: 'bg-amber-500',
    Completed: 'bg-green-500',
    Cancelled: 'bg-gray-400',
  };

  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
      onClick={() => onJobClick?.(job)}
    >
      <div className="flex items-center">
        {/* Time Column */}
        <div className="w-16 py-2 px-2 text-center border-r border-gray-100 bg-gray-50 flex-shrink-0">
          {timeDisplay ? (
            <div className="text-xs font-semibold text-gray-700">
              {timeDisplay.split(' ')[0]}
              <div className="text-[10px] text-gray-500">{timeDisplay.split(' ')[1]}</div>
            </div>
          ) : (
            <div className="text-xs text-gray-400">TBD</div>
          )}
        </div>
        
        {/* Main Content */}
        <div className="flex-1 py-2 px-3 min-w-0">
          <div className="flex items-center gap-2">
            {/* Status dot */}
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusColors[job.status] || 'bg-gray-400'}`} />
            <span className="font-medium text-gray-900 truncate text-sm">
              {job.customer?.name || 'Unknown'}
            </span>
          </div>
          <div className="text-xs text-gray-500 truncate mt-0.5">
            {job.address}
          </div>
        </div>

        {/* Price (owner only) */}
        {isOwner && job.quoteAmount > 0 && (
          <div className="pr-2 text-sm font-semibold text-green-600 flex-shrink-0">
            {formatCurrency(job.quoteAmount)}
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex items-center border-l border-gray-100 flex-shrink-0">
          {job.customer?.phone && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onJobCall?.(job);
              }}
              className="p-2.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50"
            >
              <Phone size={16} />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onJobNavigate?.(job);
            }}
            className="p-2.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50"
          >
            <MapPin size={16} />
          </button>
          {canComplete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onJobComplete?.(job);
              }}
              className="p-2.5 text-gray-400 hover:text-green-600 hover:bg-green-50"
            >
              <CheckCircle size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// MOBILE DAY VIEW
// ============================================

const MobileDayView = ({
  date,
  jobs,
  weather,
  onJobClick,
  onJobComplete,
  onJobEdit,
  onJobNavigate,
  onJobCall,
  onPrevDay,
  onNextDay,
  onStartNavigation,
  onOptimizeDay,
  onOpenTimeScheduler,
  isOwner,
  isOptimizing
}) => {
  const isToday = date === getLocalDateString();
  const dateObj = new Date(date + 'T12:00:00');
  
  // Separate jobs with time vs unscheduled
  const scheduledJobs = jobs
    .filter(j => getJobTime(j))
    .sort((a, b) => {
      const timeA = getJobTime(a) || '99:99';
      const timeB = getJobTime(b) || '99:99';
      return timeA.localeCompare(timeB);
    });
  
  const unscheduledJobs = jobs.filter(j => !getJobTime(j));
  
  // Calculate daily totals
  const dailyTotal = jobs.reduce((sum, j) => sum + (j.quoteAmount || 0), 0);

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Compact Day Navigation Header */}
      <div className="bg-white border-b border-gray-200 px-3 py-2">
        <div className="flex items-center justify-between">
          <button
            onClick={onPrevDay}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          
          <div className="text-center flex-1">
            <div className="flex items-center justify-center gap-2">
              <h2 className="font-semibold text-gray-900">
                {isToday ? 'Today' : dateObj.toLocaleDateString('en-US', { weekday: 'short' })}
              </h2>
              <span className="text-gray-500 text-sm">
                {dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              {weather && <WeatherBadge weather={weather} compact />}
            </div>
          </div>
          
          <button
            onClick={onNextDay}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        
        {/* Summary bar */}
        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-100">
          <div className="flex items-center gap-3 text-xs">
            <span className="text-gray-600 font-medium">
              {jobs.length} job{jobs.length !== 1 ? 's' : ''}
            </span>
            {isOwner && dailyTotal > 0 && (
              <span className="font-semibold text-green-600">
                {formatCurrency(dailyTotal)}
              </span>
            )}
          </div>
          {jobs.length > 0 && (
            <button
              onClick={() => onOpenTimeScheduler?.(date)}
              className="text-xs text-primary-600 font-medium flex items-center gap-1 px-2 py-1 hover:bg-primary-50 rounded"
            >
              <Clock size={12} />
              Edit Times
            </button>
          )}
        </div>
      </div>

      {/* Jobs List - Compact */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 pb-20">
        {jobs.length === 0 ? (
          <div className="text-center py-8">
            <Calendar size={36} className="mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500 text-sm">No jobs scheduled</p>
            <p className="text-xs text-gray-400 mt-1">
              Swipe to check other days
            </p>
          </div>
        ) : (
          <>
            {/* Jobs with scheduled time */}
            {scheduledJobs.map((job) => (
              <MobileJobRow
                key={job.id}
                job={job}
                onJobClick={onJobClick}
                onJobComplete={onJobComplete}
                onJobNavigate={onJobNavigate}
                onJobCall={onJobCall}
                isOwner={isOwner}
              />
            ))}
            
            {/* Jobs without time */}
            {unscheduledJobs.length > 0 && (
              <>
                <div className="text-xs text-gray-400 text-center py-1">
                  — No time set —
                </div>
                {unscheduledJobs.map((job) => (
                  <MobileJobRow
                    key={job.id}
                    job={job}
                    onJobClick={onJobClick}
                    onJobComplete={onJobComplete}
                    onJobNavigate={onJobNavigate}
                    onJobCall={onJobCall}
                    isOwner={isOwner}
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>

      {/* Action Buttons - Optimize & Navigate */}
      {jobs.length > 0 && (
        <div className="fixed bottom-16 left-0 right-0 px-3 pb-3 pt-6 bg-gradient-to-t from-gray-100 via-gray-100 to-transparent">
          {/* Show optimize button if 2+ jobs */}
          {jobs.filter(j => j.status === 'Scheduled' || j.status === 'In Progress').length >= 2 ? (
            <div className="flex gap-2">
              <button
                onClick={() => onOptimizeDay?.(date)}
                disabled={isOptimizing}
                className="flex-1 bg-white border-2 border-emerald-500 text-emerald-600 font-semibold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50"
              >
                {isOptimizing ? (
                  <Loader size={18} className="animate-spin" />
                ) : (
                  <Sparkles size={18} />
                )}
                {isOptimizing ? 'Optimizing...' : 'Optimize'}
              </button>
              <button
                onClick={() => onStartNavigation?.(date)}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
              >
                <Navigation size={18} />
                Navigate
              </button>
            </div>
          ) : (
            <button
              onClick={() => onStartNavigation?.(date)}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <Navigation size={18} />
              Start Navigation
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN CALENDAR VIEW
// ============================================

const CalendarView = ({
  jobs,
  onJobClick,
  onJobComplete,
  onJobEdit,
  onJobNavigate,
  onJobCall,
  onJobReschedule,
  onOptimizeRoute,
  onNewJob,
  isOwner = true,
  loading = false,
  initialDate = null,
  onDateChange = null,
}) => {
  // Current date and view state
  const [currentDate, setCurrentDate] = useState(() => {
    if (initialDate) {
      return new Date(initialDate + 'T12:00:00');
    }
    return new Date();
  });
  const [selectedDate, setSelectedDate] = useState(() => initialDate || getLocalDateString());
  const [calendarMode, setCalendarMode] = useState('week'); // 'week' | 'month'
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  const [industryType, setIndustryType] = useState('Other');
  
  // Day detail view (for time-slot scheduling)
  const [selectedDayDetail, setSelectedDayDetail] = useState(null);
  
  // Route optimization state
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizedData, setOptimizedData] = useState(null);
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  const [optimizeDateContext, setOptimizeDateContext] = useState(null);

  const [businessHours, setBusinessHours] = useState({
  startTime: '08:00',
  endTime: '17:00'
});
  
  // Configure drag sensor - require 8px movement before drag starts
  // This prevents clicks from being interpreted as drags
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag activates
      },
    })
  );
  
  // Drag state
  const [activeJob, setActiveJob] = useState(null);
  const [conflictModal, setConflictModal] = useState({
    isOpen: false,
    draggedJob: null,
    conflictingJobs: [],
    targetDate: null,
    targetTime: null,
    nextAvailableTime: null,
  });

  // Notify parent when date changes (for persistence)
  const updateSelectedDate = (newDate) => {
    setSelectedDate(newDate);
    onDateChange?.(newDate);
  };

  // Check for mobile on resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add this useEffect to load business hours
useEffect(() => {
  const loadBusinessHours = async () => {
    try {
      const response = await businessService.getRouteSettings();
      setBusinessHours({
        startTime: response.data.routeStartTime || '08:00',
        endTime: response.data.routeEndTime || '17:00'
      });
    } catch (error) {
      console.error('Error loading business hours:', error);
      // Keep defaults
    }
  };
  loadBusinessHours();
}, []);

  // Get dates based on view mode
  const viewDates = useMemo(() => {
    if (calendarMode === 'month') {
      return getMonthDates(currentDate);
    }
    return getWeekDates(currentDate);
  }, [currentDate, calendarMode]);
  
  // Fetch weather for the dates
  const { weatherByDate } = useWeatherForDates(viewDates);

  // Group jobs by date
  const jobsByDate = useMemo(() => {
    const grouped = {};
    viewDates.forEach(date => {
      grouped[date] = [];
    });
    
    jobs.forEach(job => {
      const jobDate = getJobDate(job);
      if (jobDate && grouped[jobDate] !== undefined) {
        grouped[jobDate].push(job);
      }
    });
    
    return grouped;
  }, [jobs, viewDates]);

  // Calculate totals
  const totals = useMemo(() => {
    let totalJobs = 0;
    let totalRevenue = 0;
    
    viewDates.forEach(date => {
      const dayJobs = jobsByDate[date] || [];
      totalJobs += dayJobs.length;
      totalRevenue += dayJobs.reduce((sum, j) => sum + (j.quoteAmount || 0), 0);
    });
    
    return { totalJobs, totalRevenue };
  }, [jobsByDate, viewDates]);

  // Navigation
  const goToPrev = () => {
    const newDate = new Date(currentDate);
    if (calendarMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setDate(newDate.getDate() - 7);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (calendarMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(getLocalDateString());
  };

  // Mobile day navigation
  const goToPrevDay = () => {
    const current = new Date(selectedDate + 'T12:00:00');
    current.setDate(current.getDate() - 1);
    const newDate = getLocalDateString(current);
    updateSelectedDate(newDate);
    setCurrentDate(current);
  };

  const goToNextDay = () => {
    const current = new Date(selectedDate + 'T12:00:00');
    current.setDate(current.getDate() + 1);
    const newDate = getLocalDateString(current);
    updateSelectedDate(newDate);
    setCurrentDate(current);
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    const { active } = event;
    const job = active.data.current?.job;
    if (job) {
      setActiveJob(job);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveJob(null);

    if (!over) return;

    const draggedJob = active.data.current?.job;
    const targetDate = over.data.current?.date;

    if (!draggedJob || !targetDate) return;

    // Get current job date and time (LOCAL time as stored in DB)
    const currentDate = getJobDate(draggedJob);
    const currentTime = getJobTime(draggedJob);

    // If dropping on same date, don't do anything (click, not drag)
    if (currentDate === targetDate) {
      console.log('Dropped on same date, ignoring');
      return;
    }

    console.log(`[CalendarView] Dragging job ${draggedJob.id} from ${currentDate} to ${targetDate}, keeping time: ${currentTime}`);

    // Check for conflicts
    const targetDayJobs = jobsByDate[targetDate] || [];
    const conflicts = checkConflicts(draggedJob, targetDayJobs);

    if (conflicts.length > 0) {
      setConflictModal({
        isOpen: true,
        draggedJob,
        conflictingJobs: conflicts,
        targetDate,
        targetTime: currentTime,
        nextAvailableTime: findNextAvailableTime(targetDayJobs, draggedJob),
      });
    } else {
      // Pass local time directly - no conversion needed
      onJobReschedule?.(draggedJob, targetDate, currentTime);
    }
  };

  // Check for time conflicts
  const checkConflicts = (draggedJob, targetDayJobs) => {
    const draggedTime = getJobTime(draggedJob);
    if (!draggedTime) return [];
    
    const draggedDuration = draggedJob.estimatedDurationMinutes || 60;
    const draggedStart = timeToMinutes(draggedTime);
    const draggedEnd = draggedStart + draggedDuration;

    return targetDayJobs.filter(job => {
      if (job.id === draggedJob.id) return false;
      
      const jobTime = getJobTime(job);
      if (!jobTime) return false;
      
      const jobDuration = job.estimatedDurationMinutes || 60;
      const jobStart = timeToMinutes(jobTime);
      const jobEnd = jobStart + jobDuration;

      return draggedStart < jobEnd && draggedEnd > jobStart;
    });
  };

  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const findNextAvailableTime = (dayJobs, draggedJob) => {
    const draggedDuration = draggedJob.estimatedDurationMinutes || 60;
    const scheduledJobs = dayJobs
      .filter(j => getJobTime(j) && j.id !== draggedJob.id)
      .map(j => ({
        start: timeToMinutes(getJobTime(j)),
        end: timeToMinutes(getJobTime(j)) + (j.estimatedDurationMinutes || 60),
      }))
      .sort((a, b) => a.start - b.start);

    let candidateStart = 8 * 60;
    const dayEnd = 18 * 60;

    for (const job of scheduledJobs) {
      if (candidateStart + draggedDuration <= job.start) {
        break;
      }
      candidateStart = Math.max(candidateStart, job.end);
    }

    if (candidateStart + draggedDuration <= dayEnd) {
      const hours = Math.floor(candidateStart / 60);
      const minutes = candidateStart % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    return null;
  };

  const handleConflictConfirm = (choice, dontWarnAgain) => {
    if (dontWarnAgain) {
      localStorage.setItem('calendar_skip_conflict_warning', 'true');
    }

    if (choice === 'schedule-anyway') {
      onJobReschedule?.(
        conflictModal.draggedJob,
        conflictModal.targetDate,
        conflictModal.targetTime
      );
    }

    setConflictModal({ ...conflictModal, isOpen: false });
  };

  const handleConflictAlternative = (newTime) => {
    onJobReschedule?.(
      conflictModal.draggedJob,
      conflictModal.targetDate,
      newTime
    );
    setConflictModal({ ...conflictModal, isOpen: false });
  };

  // Header display
  const headerDisplay = useMemo(() => {
    if (calendarMode === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    const weekDates = getWeekDates(currentDate);
    const start = new Date(weekDates[0] + 'T12:00:00');
    const end = new Date(weekDates[6] + 'T12:00:00');
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  }, [currentDate, calendarMode]);

  // Handle "Start Navigation" - Uses SCHEDULED order (not optimized)
const handleStartNavigation = (dateStr) => {
  const dayJobs = jobsByDate[dateStr] || [];
  
  const scheduledJobs = [...dayJobs]
    .filter(j => j.status === 'Scheduled' || j.status === 'In Progress')
    .sort((a, b) => {
      // Sort by scheduled time
      const timeA = getJobTime(a) || '99:99';
      const timeB = getJobTime(b) || '99:99';
      return timeA.localeCompare(timeB);
    });

  if (scheduledJobs.length === 0) {
    alert('No scheduled jobs for this day');
    return;
  }

  // For single job, navigate directly
  if (scheduledJobs.length === 1) {
    const job = scheduledJobs[0];
    const address = encodeURIComponent(`${job.address}, ${job.city}, ${job.state} ${job.zipCode}`);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
    return;
  }

  // For multiple jobs, build Google Maps URL with SCHEDULED order
  const addresses = scheduledJobs.map(job => {
    const parts = [job.address, job.city, job.state, job.zipCode].filter(Boolean);
    return encodeURIComponent(parts.join(', '));
  });

  const destination = addresses[addresses.length - 1];
  const waypoints = addresses.slice(0, -1).join('|');
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}&waypoints=${waypoints}`;
  
  window.open(mapsUrl, '_blank');
};

  // Handle "Optimize Day" - Shows preview modal with optimized route
  const handleOptimizeDay = async (dateStr) => {
    const dayJobs = jobsByDate[dateStr] || [];
    
    const scheduledJobs = [...dayJobs]
      .filter(j => j.status === 'Scheduled' || j.status === 'In Progress');

    if (scheduledJobs.length < 2) {
      alert('Need at least 2 scheduled jobs to optimize');
      return;
    }

    setOptimizeDateContext(dateStr);
    setShowOptimizeModal(true);
    setIsOptimizing(true);
    setOptimizedData(null);

    try {
      // Get current GPS location
      const position = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation not supported'));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
          (err) => reject(err),
          { enableHighAccuracy: true, timeout: 10000 }
        );
      });

      // Call optimize API with GPS location
      const response = await routingService.optimizeDay(dateStr, position);
      setOptimizedData(response.data);
    } catch (error) {
      console.error('Error optimizing route:', error);
      setOptimizedData(null);
    } finally {
      setIsOptimizing(false);
    }
  };

  // Handle navigation from optimize modal
  const handleOptimizedNavigation = () => {
    if (!optimizedData?.optimizedJobs?.length) return;

    const addresses = optimizedData.optimizedJobs.map(job => {
      const parts = [job.address, job.city, job.state, job.zipCode].filter(Boolean);
      return encodeURIComponent(parts.join(', '));
    });

    const destination = addresses[addresses.length - 1];
    const waypoints = addresses.slice(0, -1).join('|');
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}&waypoints=${waypoints}`;
    
    window.open(mapsUrl, '_blank');
    setShowOptimizeModal(false);
  };

  const todayStr = getLocalDateString();
  const currentMonth = currentDate.getMonth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader size={32} className="animate-spin text-primary-600" />
      </div>
    );
  }

  // Mobile: Show day view OR day detail view
  // Mobile: Show day view OR day detail view
  if (isMobile) {
    if (selectedDayDetail) {
      return (
        <DayDetailView
          date={selectedDayDetail}
          jobs={jobsByDate[selectedDayDetail] || []}
          weather={weatherByDate[selectedDayDetail]}
          onBack={() => setSelectedDayDetail(null)}
          onJobClick={onJobClick}
          onJobComplete={onJobComplete}
          onJobEdit={onJobEdit}
          onJobNavigate={onJobNavigate}
          onJobCall={onJobCall}
          onJobReschedule={onJobReschedule}
          onOptimizeRoute={onOptimizeRoute}
          onNewJob={onNewJob}
          isOwner={isOwner}
        />
      );
    }

    return (
      <>
        <MobileDayView
          date={selectedDate}
          jobs={jobsByDate[selectedDate] || []}
          weather={weatherByDate[selectedDate]}
          onJobClick={onJobClick}
          onJobComplete={onJobComplete}
          onJobEdit={onJobEdit}
          onJobNavigate={onJobNavigate}
          onJobCall={onJobCall}
          onPrevDay={goToPrevDay}
          onNextDay={goToNextDay}
          onStartNavigation={handleStartNavigation}
          onOptimizeDay={handleOptimizeDay}
          onOpenTimeScheduler={setSelectedDayDetail}
          isOwner={isOwner}
          isOptimizing={isOptimizing}
        />

        {/* Optimize Preview Modal - Mobile */}
        <OptimizePreviewModal
          isOpen={showOptimizeModal}
          onClose={() => {
            setShowOptimizeModal(false);
            setOptimizedData(null);
          }}
          currentJobs={jobsByDate[optimizeDateContext] || []}
          optimizedData={optimizedData}
          onStartNavigation={handleOptimizedNavigation}
          isOwner={isOwner}
          isOptimizing={isOptimizing}
        />

        <ConflictModal
          isOpen={conflictModal.isOpen}
          onClose={() => setConflictModal({ ...conflictModal, isOpen: false })}
          onConfirm={handleConflictConfirm}
          onSelectAlternative={handleConflictAlternative}
          draggedJob={conflictModal.draggedJob}
          conflictingJobs={conflictModal.conflictingJobs}
          targetTime={conflictModal.targetTime}
          targetDate={conflictModal.targetDate}
          nextAvailableTime={conflictModal.nextAvailableTime}
          isOwner={isOwner}
        />
      </>
    );
  }

  // Desktop: Show week or month view
  return (
    <>
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {/* Day Detail View - Full screen overlay when a day is selected */}
      {selectedDayDetail && (
  <div className="fixed inset-0 z-50 bg-white">
    <DayDetailView
      date={selectedDayDetail}
      jobs={jobsByDate[selectedDayDetail] || []}
      weather={weatherByDate[selectedDayDetail]}
      businessStartTime={businessHours.startTime}
      businessEndTime={businessHours.endTime}
      onBack={() => setSelectedDayDetail(null)}
      onJobClick={onJobClick}
      onJobComplete={onJobComplete}
      onJobEdit={onJobEdit}
      onJobNavigate={onJobNavigate}
      onJobCall={onJobCall}
      onJobReschedule={onJobReschedule}
      onOptimizeRoute={() => handleOptimizeDay(selectedDayDetail)}  // ✅ FIXED - shows preview modal
      onNewJob={onNewJob}
      isOwner={isOwner}
    />
  </div>
)}

      <div className="flex flex-col h-full">
        {/* Navigation Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrev}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={goToNext}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight size={20} />
            </button>
            <h2 className="text-lg font-semibold text-gray-900 ml-2">
              {headerDisplay}
            </h2>
          </div>

          <div className="flex items-center gap-3">
            {/* Summary */}
            <div className="text-sm text-gray-500 hidden sm:block">
              {totals.totalJobs} jobs
              {isOwner && totals.totalRevenue > 0 && (
                <span className="ml-2 font-medium text-gray-700">
                  {formatCurrency(totals.totalRevenue)}
                </span>
              )}
            </div>

            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              Today
            </button>

            {/* Week/Month Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setCalendarMode('week')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  calendarMode === 'week'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Week
              </button>
              <button
                onClick={() => setCalendarMode('month')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  calendarMode === 'month'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Month
              </button>
            </div>
          </div>
        </div>

        {/* Status Legend */}
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
          <StatusLegend />
        </div>

        {/* Calendar Content */}
        {calendarMode === 'week' ? (
          /* Week View */
          <div className="flex-1 flex overflow-hidden">
            {viewDates.map((date) => (
              <DroppableDayColumn
                key={date}
                date={date}
                isToday={date === todayStr}
              >
                <DayColumnContent
                  date={date}
                  jobs={jobsByDate[date] || []}
                  weather={weatherByDate[date]}
                  isToday={date === todayStr}
                  onJobClick={onJobClick}
                  onJobComplete={onJobComplete}
                  onJobEdit={onJobEdit}
                  onJobNavigate={onJobNavigate}
                  onJobCall={onJobCall}
                  onDayClick={setSelectedDayDetail}
                  isOwner={isOwner}
                />
              </DroppableDayColumn>
            ))}
          </div>
        ) : (
          /* Month View */
          <div className="flex-1 overflow-auto">
            {/* Day of week headers */}
            <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7">
              {viewDates.map((date) => {
                const dateObj = new Date(date + 'T12:00:00');
                const isCurrentMonth = dateObj.getMonth() === currentMonth;
                
                return (
                  <DroppableDayCell
                    key={date}
                    date={date}
                    isToday={date === todayStr}
                    isCurrentMonth={isCurrentMonth}
                  >
                    <DayCellContent
                      date={date}
                      jobs={jobsByDate[date] || []}
                      weather={weatherByDate[date]}
                      isToday={date === todayStr}
                      isCurrentMonth={isCurrentMonth}
                      onJobClick={onJobClick}
                      onDayClick={setSelectedDayDetail}
                      isOwner={isOwner}
                    />
                  </DroppableDayCell>
                );
              })}
            </div>
          </div>
        )}

        {/* Drag Overlay */}
        <DragOverlay>
          {activeJob && (
            <JobBlock
              job={activeJob}
              compact
              isDragging
              isOwner={isOwner}
            />
          )}
        </DragOverlay>
      </div>

      {/* Conflict Modal */}
      <ConflictModal
        isOpen={conflictModal.isOpen}
        onClose={() => setConflictModal({ ...conflictModal, isOpen: false })}
        onConfirm={handleConflictConfirm}
        onSelectAlternative={handleConflictAlternative}
        draggedJob={conflictModal.draggedJob}
        conflictingJobs={conflictModal.conflictingJobs}
        targetTime={conflictModal.targetTime}
        targetDate={conflictModal.targetDate}
        nextAvailableTime={conflictModal.nextAvailableTime}
        isOwner={isOwner}
      />
    </DndContext>
    
    <OptimizePreviewModal
          isOpen={showOptimizeModal}
          onClose={() => {
            setShowOptimizeModal(false);
            setOptimizedData(null);
          }}
          currentJobs={jobsByDate[optimizeDateContext] || []}
          optimizedData={optimizedData}
          onStartNavigation={handleOptimizedNavigation}
          isOwner={isOwner}
          isOptimizing={isOptimizing}
        />
        </>
  );
};

export default CalendarView;